// src/components/wellfinder/QueueFilters.jsx
//
// Tier (multi), status, and the sort toggle.
//
// THE OPERATING AOI IS NOT HERE, deliberately. It sits beside the page title
// instead, because it is not the same kind of control: tier and status NARROW a
// queue, the AOI says WHICH queue. A reviewer picks it once when they sit down
// and then works within it, so it belongs with the page's identity rather than
// in a row of chips they toggle while working — where it was, and where it read
// as one more optional refinement nobody had to notice.
//
// A TOGGLE, NOT COLUMN HEADERS. The two sorts are not two orderings of the same
// idea — 'thorough' walks C first because C is where the findings are, and
// 'fastest' walks A first because A is where the volume is. Those are two review
// strategies, and dressing them as sortable columns would imply the analyst can
// sort by distance or operator, which the cursor contract cannot support.

const TIERS = [
  { key: "c", label: "C" },
  { key: "b", label: "B" },
  { key: "a_prime", label: "A′" },
  { key: "a", label: "A" },
  { key: "d", label: "D" },
];

const STATUSES = ["pending", "approved", "rejected", "needs_review"];

function Chip({ active, onClick, children, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-pressed={active}
      className={[
        "rounded px-2 py-0.5 text-xs ring-1 transition-colors",
        active
          ? "text-fg-primary ring-accent-action/50"
          : "bg-white/[0.03] text-fg-secondary ring-white/10 hover:bg-white/[0.06]",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

export default function QueueFilters({ filters, onChange, claimsPausedReason = null }) {
  // No tiers selected means ALL tiers, which is what the server does with an
  // empty tier list. Rendering it as "every chip lit" would be a lie the first
  // time someone deselects one.
  const tierActive = (t) => filters.tiers.includes(t);

  const toggleTier = (t) => {
    const next = tierActive(t)
      ? filters.tiers.filter((x) => x !== t)
      : [...filters.tiers, t];
    onChange({ tiers: next });
  };

  const toggleStatus = (s) => {
    const next = filters.statuses.includes(s)
      ? filters.statuses.filter((x) => x !== s)
      : [...filters.statuses, s];
    // An empty status list would ask the server for nothing. Refuse the last
    // deselection rather than rendering an empty queue that looks like an
    // exhausted one.
    if (!next.length) return;
    onChange({ statuses: next });
  };

  return (
    <div className="mb-3">
      {/* ── CLAIMING PAUSED ────────────────────────────────────────────────
          Rendered beside the chips that cause it, not in the page header:
          the status filter is what pauses claiming, so the notice belongs
          where the analyst can act on it. role="status" rather than "alert" —
          this is a standing condition of the current filter, not an event, and
          an assertive live region would interrupt a screen reader mid-record.
          It says what is NOT happening, because an analyst who cannot see that
          claims stopped has no reason to expect another analyst to beat them
          to a record. */}
      {claimsPausedReason ? (
        <div
          role="status"
          className="mb-2 flex items-start gap-2 rounded px-2.5 py-1.5
                     bg-amber-400/10 ring-1 ring-amber-400/30"
        >
          <span aria-hidden="true" className="text-amber-300 text-xs leading-5">&#9888;</span>
          <span className="text-[11px] text-amber-200/90 leading-5">{claimsPausedReason}</span>
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
      <div className="flex items-center gap-1.5">
        <span className="text-fg-secondary text-[11px] font-mono uppercase tracking-wide mr-1">
          Tier
        </span>
        {TIERS.map((t) => (
          <Chip
            key={t.key}
            active={tierActive(t.key)}
            onClick={() => toggleTier(t.key)}
            title={t.key === "c" ? "No registry candidate — highest-value review" : undefined}
          >
            {t.label}
          </Chip>
        ))}
        {filters.tiers.length === 0 && (
          <span className="text-fg-secondary/60 text-[11px] ml-1">all</span>
        )}
      </div>

      <div className="flex items-center gap-1.5">
        <span className="text-fg-secondary text-[11px] font-mono uppercase tracking-wide mr-1">
          Status
        </span>
        {STATUSES.map((s) => (
          <Chip key={s} active={filters.statuses.includes(s)} onClick={() => toggleStatus(s)}>
            {s}
          </Chip>
        ))}
      </div>

      <div className="flex items-center gap-1.5">
        <span className="text-fg-secondary text-[11px] font-mono uppercase tracking-wide mr-1">
          Order
        </span>
        <Chip
          active={filters.sort === "thorough"}
          onClick={() => onChange({ sort: "thorough" })}
          title="C, B, A′, A — hardest first"
        >
          thorough
        </Chip>
        <Chip
          active={filters.sort === "fastest"}
          onClick={() => onChange({ sort: "fastest" })}
          title="A, A′, B, C — highest volume first"
        >
          fastest
        </Chip>
      </div>
      </div>
    </div>
  );
}
